import { TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'

export interface Report {
  /** Undefined represents a default column */
  columns?: (Report.Column | undefined)[]
  /** Undefined represents an empty row */
  rows: (Report.Row | undefined)[]
  /**
   * Number of leading rows to keep frozen/visible when scrolling (Excel
   * freeze panes)
   *
   * @default no rows frozen
   */
  freezeRowCount?: number
}
export namespace Report {
  export const isReport = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Report>({
      columns: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(TypeChecker.Factory.optional(Column.isColumn)),
      ),
      freezeRowCount: TypeChecker.Factory.optional(TypeChecker.isNumber),
      rows: TypeChecker.Factory.list(TypeChecker.Factory.optional(Row.isRow)),
    }),
  )

  export interface Column {
    /**
     * Scalar applied to the standard width
     *
     * @default 1
     * @example
     * const standardWidthInPixels = 80
     * column.width = 1.25
     * const columnWidthInPixels = standardWidthInPixels * column.width
     */
    width?: number
  }
  export namespace Column {
    export const isColumn = TypeChecker.Factory.object<Column>({
      width: TypeChecker.Factory.optional(TypeChecker.isNumber),
    })
  }

  export interface Row {
    /** Undefined represents an empty cell */
    cells: (Cell | undefined)[]
  }
  export namespace Row {
    export const isRow = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Row>({
        cells: TypeChecker.Factory.list(
          TypeChecker.Factory.optional(Cell.isCell),
        ),
      }),
    )
  }

  export interface Cell {
    /**
     * @default no content
     */
    content?: core.common.value.WithInfo.Strict
    /**
     * @default no special style
     */
    style?: Cell.Style
    /**
     * @default 1
     */
    columnSpan?: number
    /**
     * @default 0
     */
    indents?: number
    /**
     * @default
     * ```ts
     * // For numbers
     * TextAlignment.Right
     * // For boolean
     * TextAlignment.Center
     * // For everything else
     * TextAlignment.Left
     * ```
     */
    align?: Cell.TextAlignment
  }
  export namespace Cell {
    export const isCell = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Cell>({
        align: TypeChecker.Factory.optional(TextAlignment.isTextAlignment),
        columnSpan: TypeChecker.Factory.optional(TypeChecker.isNumber),
        content: TypeChecker.Factory.optional(
          core.common.value.WithInfo.Strict.isStrict,
        ),
        indents: TypeChecker.Factory.optional(TypeChecker.isNumber),
        style: TypeChecker.Factory.optional(Style.isStyle),
      }),
    )

    /**
     * A {@link Cell} with default values for the properties that have defaults
     */
    export type WithDefaults = Partial.Semi<Required<Cell>, 'content' | 'style'>
    export namespace WithDefaults {
      // TODO: use this on the frontend
      /**
       * Creates a {@link Cell.WithDefaults} from a given {@link Cell} (or `undefined`)
       */
      export function from(cell: Readonly<Cell> | undefined): WithDefaults {
        const type =
          cell?.content &&
          core.common.value.types.Intrinsic.from<typeof cell.content>(
            cell.content,
          )
        let align = cell?.align
        if (!align) {
          if (core.common.value.types.Intrinsic.ForNumber.isForNumber(type)) {
            align = TextAlignment.Right
          } else if (type === core.common.value.types.Intrinsic.Boolean) {
            align = TextAlignment.Center
          } else {
            align = TextAlignment.Left
          }
        }
        return {
          align,
          columnSpan: cell?.columnSpan ?? 1,
          content: cell?.content,
          indents: cell?.indents ?? 0,
          style: cell?.style,
        }
      }
    }

    /**
     * Used when a {@link Cell} should have some special style applied to it
     *
     * Not all {@link Cell} will have a {@link Style}
     */
    export enum Style {
      ReportHeader = 'ReportHeader',
      SectionHeader = 'SectionHeader',
      SubsectionHeader = 'SubsectionHeader',
      ColumnHeader = 'ColumnHeader',

      Label = 'Label',
      Subtotal = 'Subtotal',
      GrandTotal = 'GrandTotal',

      /** A blank cell with a thin line along its bottom, between records */
      Divider = 'Divider',
    }
    // Needs to be before namespace
    const STYLES: ReadonlySet<Style> = new Set(Object.values(Style) as Style[])
    export namespace Style {
      export const ALL = Array.from(STYLES) as readonly Style[]
      export const isStyle = TypeChecker.Factory.oneOf(STYLES)
    }

    export enum TextAlignment {
      Left = 'start',
      Right = 'end',
      Center = 'center',
    }
    // Needs to be before namespace
    const TEXT_ALIGNMENTS: ReadonlySet<TextAlignment> = new Set(
      Object.values(TextAlignment) as TextAlignment[],
    )
    export namespace TextAlignment {
      export const ALL = Array.from(TEXT_ALIGNMENTS) as readonly TextAlignment[]
      export const isTextAlignment = TypeChecker.Factory.oneOf(TEXT_ALIGNMENTS)
    }
  }
}
